const TYPE_WORDS = {
    receipt: 'document',
    receipts: 'document',
    invoice: 'document',
    invoices: 'document',
    document: 'document',
    documents: 'document',
    statement: 'document',
    statements: 'document',
    pdf: 'document',
    pdfs: 'document',
    record: 'transaction',
    records: 'transaction',
    transaction: 'transaction',
    transactions: 'transaction',
    expense: 'transaction',
    expenses: 'transaction',
    purchase: 'transaction',
    purchases: 'transaction',
    supplier: 'vendor',
    suppliers: 'vendor',
    vendor: 'vendor',
    vendors: 'vendor',
    customer: 'customer',
    customers: 'customer',
    job: 'job',
    jobs: 'job',
    equipment: 'equipment',
    unit: 'equipment',
    units: 'equipment',
};
const STOP_WORDS = new Set([
    'the',
    'a',
    'an',
    'of',
    'in',
    'on',
    'at',
    'to',
    'for',
    'from',
    'with',
    'by',
    'me',
    'my',
    'our',
    'all',
    'any',
    'show',
    'find',
    'get',
    'everything',
    'and',
    'that',
    'this',
    'those',
    'these',
    'was',
    'is',
    'are',
    'were',
    'paid',
]);
const MONTHS = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
];
const AMOUNT = String.raw `\$?([\d][\d,]*(?:\.\d{1,2})?)k?`;
function toMinor(raw, hadK) {
    const value = Number(raw.replace(/,/g, ''));
    return String(Math.round(value * (hadK ? 1000 : 1) * 100));
}
function formatDollars(minor) {
    const dollars = Number(minor) / 100;
    return `$${dollars.toLocaleString('en-CA', { maximumFractionDigits: 2 })}`;
}
function iso(date) {
    return date.toISOString().slice(0, 10);
}
export function parseQuery(text, now = new Date()) {
    let rest = ` ${text.toLowerCase().trim()} `;
    const filters = {};
    const chips = [];
    const claim = (pattern, onMatch) => {
        const match = rest.match(pattern);
        if (match !== null) {
            onMatch(match);
            rest = rest.replace(pattern, ' ');
        }
    };
    /* ---- amounts. Order matters: bounded phrasings claim their number before
       the bare-number rule can read it as a magnitude. ---- */
    claim(new RegExp(String.raw `(?:over|above|more than|at least)\s+${AMOUNT}`), (m) => {
        const minor = toMinor(m[1] ?? '0', /k[\s]*$/.test(m[0]));
        filters.amountMinMinor = minor;
        chips.push(`over ${formatDollars(minor)}`);
    });
    claim(new RegExp(String.raw `(?:under|below|less than|at most)\s+${AMOUNT}`), (m) => {
        const minor = toMinor(m[1] ?? '0', /k[\s]*$/.test(m[0]));
        filters.amountMaxMinor = minor;
        chips.push(`under ${formatDollars(minor)}`);
    });
    claim(new RegExp(String.raw `(?:around|about|approx(?:imately)?|~)\s+${AMOUNT}`), (m) => {
        const minor = toMinor(m[1] ?? '0', /k[\s]*$/.test(m[0]));
        filters.amountAroundMinor = minor;
        chips.push(`around ${formatDollars(minor)}`);
    });
    // A bare dollar figure or standalone number ≥ 2 digits reads as "around".
    if (filters.amountMinMinor === undefined &&
        filters.amountMaxMinor === undefined &&
        filters.amountAroundMinor === undefined) {
        claim(new RegExp(String.raw `(?<=\s)\$([\d][\d,]*(?:\.\d{1,2})?)k?(?=\s)|(?<=\s)(\d{2,}(?:\.\d{1,2})?)(?=\s)`), (m) => {
            const raw = m[1] ?? m[2] ?? '0';
            const minor = toMinor(raw, /k(?=\s)/.test(m[0]));
            filters.amountAroundMinor = minor;
            chips.push(`around ${formatDollars(minor)}`);
        });
    }
    /* ---- dates ---- */
    const year = now.getUTCFullYear();
    claim(/this year/, () => {
        filters.dateFrom = `${String(year)}-01-01`;
        chips.push(`since Jan 1, ${String(year)}`);
    });
    claim(/last year/, () => {
        filters.dateFrom = `${String(year - 1)}-01-01`;
        filters.dateTo = `${String(year - 1)}-12-31`;
        chips.push(`in ${String(year - 1)}`);
    });
    claim(/this month/, () => {
        filters.dateFrom = iso(new Date(Date.UTC(year, now.getUTCMonth(), 1)));
        chips.push('this month');
    });
    claim(/last month/, () => {
        filters.dateFrom = iso(new Date(Date.UTC(year, now.getUTCMonth() - 1, 1)));
        filters.dateTo = iso(new Date(Date.UTC(year, now.getUTCMonth(), 0)));
        chips.push('last month');
    });
    for (const [index, month] of MONTHS.entries()) {
        claim(new RegExp(String.raw `(?:in |from |since )?\b${month}\b(?:\s+(\d{4}))?`), (m) => {
            const monthYear = m[1] === undefined ? (index <= now.getUTCMonth() ? year : year - 1) : Number(m[1]);
            const from = new Date(Date.UTC(monthYear, index, 1));
            const since = m[0].trimStart().startsWith('since');
            filters.dateFrom = iso(from);
            if (!since)
                filters.dateTo = iso(new Date(Date.UTC(monthYear, index + 1, 0)));
            const label = `${month.charAt(0).toUpperCase()}${month.slice(1)} ${String(monthYear)}`;
            chips.push(since ? `since ${label}` : `in ${label}`);
        });
    }
    /* ---- object types ---- */
    const types = new Set();
    for (const [word, type] of Object.entries(TYPE_WORDS)) {
        const pattern = new RegExp(String.raw `(?<=\s)${word}(?=\s)`);
        if (pattern.test(rest)) {
            types.add(type);
            rest = rest.replace(pattern, ' ');
        }
    }
    if (types.size > 0) {
        filters.entityTypes = Array.from(types);
        chips.push(filters.entityTypes.join(', '));
    }
    /* ---- whatever remains is text ---- */
    const words = rest
        .split(/[^a-z0-9']+/)
        .map((word) => word.trim())
        .filter((word) => word.length >= 2 && !STOP_WORDS.has(word))
        .slice(0, 8);
    if (words.length > 0) {
        filters.words = words;
        chips.unshift(`“${words.join(' ')}”`);
    }
    return { filters, chips };
}
//# sourceMappingURL=query-parser.js.map